import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, Headphones, Award, Sparkles, Search } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import FadeIn from "@/components/FadeIn";
import TourCard, { TourCardSkeleton } from "@/components/TourCard";

export default function Home() {
  const settings = useSettings();
  const nav = useNavigate();
  const [tours, setTours] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [slide, setSlide] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/tours", { params: { featured: true } }).then((r) => setTours(r.data)).catch(() => setTours([]));
    api.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const slides = (destinations.length ? destinations.slice(0, 6).map((d) => d.image) : [settings?.hero_image]).filter(Boolean);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [slides.length]);

  const onSearch = (e) => { e.preventDefault(); nav(`/tours?search=${encodeURIComponent(search)}`); };

  const schema = {
    "@context": "https://schema.org", "@type": "TravelAgency",
    name: settings?.brand_name || "Happy Holidays Wale",
    description: settings?.hero_subtitle, telephone: settings?.phone, email: settings?.email,
  };

  return (
    <>
      <SEO title="Happy Holidays Wale | Premium Tour Packages & Holidays" description={settings?.hero_subtitle} image={settings?.hero_image} schema={schema} />

      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden">
        {slides.map((img, i) => (
          <img key={i} src={img} alt="Dream holiday" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0"}`} />
        ))}
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,34,68,0.92), rgba(0,34,68,0.6), rgba(0,34,68,0.2))" }} />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 md:px-8">
          <div className="max-w-2xl fade-up">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md">
              <Sparkles className="h-4 w-4 text-brandorange" /> Handcrafted Premium Holidays
            </span>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {settings?.hero_title || "Your Dream Holiday, Perfectly Planned"}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">{settings?.hero_subtitle}</p>

            <form onSubmit={onSearch} className="mt-8 flex max-w-xl overflow-hidden rounded-full bg-white p-1.5 shadow-xl">
              <input data-testid="hero-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search destinations or tours..." className="flex-1 bg-transparent px-4 text-navy outline-none" />
              <button data-testid="hero-search-btn" className="btn-gradient flex items-center gap-2 rounded-full px-6 py-3 font-bold"><Search className="h-4 w-4" /> Search</button>
            </form>

            <div className="mt-6 flex flex-wrap gap-4">
              <Link to="/tours" data-testid="hero-explore-btn" className="btn-gradient flex items-center gap-2 rounded-full px-7 py-4 text-base font-bold">Explore Packages <ArrowRight className="h-5 w-5" /></Link>
              <Link to="/enquiry" data-testid="hero-enquire-btn" className="rounded-full border border-white/40 bg-white/10 px-7 py-4 text-base font-bold text-white backdrop-blur-md transition-colors hover:bg-white/20">Plan My Trip</Link>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 flex -translate-x-1/2 gap-2 md:left-8 md:translate-x-0">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`} className={`h-2 rounded-full transition-all ${i === slide ? "w-8 bg-brandorange" : "w-2 bg-white/50"}`} />
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-gray-100 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-8 md:grid-cols-4 md:px-8">
          {[
            { Icon: ShieldCheck, t: "100% Secure", s: "Trusted bookings" },
            { Icon: Award, t: "Best Price", s: "Guaranteed value" },
            { Icon: Headphones, t: "24/7 Support", s: "Always here for you" },
            { Icon: Star, t: "5000+ Happy", s: "Travelers served" },
          ].map(({ Icon, t, s }) => (
            <div key={t} className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brandorange/10 text-brandorange"><Icon className="h-6 w-6" /></span>
              <div><p className="font-display font-bold text-navy">{t}</p><p className="text-xs text-gray-500">{s}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* DESTINATIONS - horizontal scroll on mobile */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8">
        <FadeIn className="mb-8 flex items-end justify-between">
          <div>
            <p className="font-semibold text-brandorange">Top Destinations</p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">Where will you go next?</h2>
          </div>
          <Link to="/destinations" className="hidden text-sm font-semibold text-navy hover:text-brandorange sm:block">View all →</Link>
        </FadeIn>
        <div className="no-scrollbar flex snap-x gap-5 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible lg:grid-cols-4">
          {destinations.slice(0, 8).map((d) => (
            <Link key={d.id} to={`/destination/${d.slug}`} data-testid={`home-destination-${d.slug}`} className="group relative min-w-[75%] snap-start overflow-hidden rounded-3xl sm:min-w-[45%] md:min-w-0">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={d.image} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <h3 className="font-display text-2xl font-bold text-white">{d.name}</h3>
                <p className="text-sm text-white/70">{d.country}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED TOURS */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <FadeIn className="mb-8 text-center">
            <p className="font-semibold text-brandorange">Featured Packages</p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">Our Most-Loved Holidays</h2>
          </FadeIn>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {tours === null
              ? Array.from({ length: 3 }).map((_, i) => <TourCardSkeleton key={i} />)
              : tours.map((t, i) => <FadeIn key={t.id} delay={i * 0.05}><TourCard tour={t} /></FadeIn>)}
          </div>
          <div className="mt-10 text-center">
            <Link to="/tours" data-testid="home-view-all-tours" className="inline-flex items-center gap-2 rounded-full border-2 border-navy px-7 py-3.5 font-bold text-navy transition-colors hover:bg-navy hover:text-white">
              View All Packages <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 md:px-8">
          <FadeIn className="mb-8 text-center">
            <p className="font-semibold text-brandorange">Traveler Stories</p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">Loved by thousands</h2>
          </FadeIn>
          <div className="grid gap-6 md:grid-cols-3">
            {testimonials.slice(0, 3).map((t, i) => (
              <FadeIn key={t.id} delay={i * 0.05} className="rounded-3xl bg-white p-7 card-soft">
                <div className="mb-3 flex gap-1">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="h-4 w-4 fill-brandorange text-brandorange" />)}</div>
                <p className="text-gray-600">"{t.text}"</p>
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <p className="font-display font-bold text-navy">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.location} • {t.tour}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
